import React, { useState, useEffect, useRef } from 'react';
import '../styles/Pago.css';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { FaMoneyBillWave, FaCreditCard, FaUniversity } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';


interface Producto {
  nombre: string;
  imagen: string;
  color: string;
  precio: number;
  cantidad: number;
  // (el teléfono es de la orden, no del producto; lo dejamos sin usar aquí)
}

const center = { lat: -33.4372, lng: -70.6506 };
const origen = 'Fray Camilo Henríquez 440, Santiago, Chile';

const Pago: React.FC = () => {
  const [direccion, setDireccion] = useState('');
  const [numero, setNumero] = useState('');
  const [tipoEntrega, setTipoEntrega] = useState<'despacho' | 'retiro'>('despacho');
  const [carrito, setCarrito] = useState<Producto[]>([]);
  const [costoDespacho, setCostoDespacho] = useState(0);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [metodoPago, setMetodoPago] = useState<'mercadopago' | 'transferencia' | 'efectivo' | 'flow'>('mercadopago');

  const [telefono, setTelefono] = useState(''); // <-- TELÉFONO
  const [codigoDescuento, setCodigoDescuento] = useState('');
  const [estadoCupon, setEstadoCupon] = useState<'valido' | 'invalido' | null>(null);

  // ----- NUEVO: distancia y banderas -----
  const [distanciaKm, setDistanciaKm] = useState<number | null>(null);
  const [sinCobertura, setSinCobertura] = useState(false);
  const [envioGratis, setEnvioGratis] = useState(false);
  // ---------------------------------------

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const carritoGuardado = localStorage.getItem('carrito');
    if (carritoGuardado) setCarrito(JSON.parse(carritoGuardado));
  }, []);

  // ======= ACTUALIZADO: reglas pedidas =======
  const calcularCosto = (km: number) => {
    if (km < 5) return 0;       // envío gratis
    if (km <= 8) return 3500;
    if (km <= 15) return 4000;
    return -1;                  // sin cobertura
  };

  const calcularDistancia = (destino: string) => {
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origen],
        destinations: [destino],
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (status === 'OK' && response) {
          const distanciaMetros = response.rows[0].elements[0].distance.value;
          const km = distanciaMetros / 1000;

          setDistanciaKm(km);

          const costo = calcularCosto(km);
          if (costo === -1) {
            setSinCobertura(true);
            setEnvioGratis(false);
            setCostoDespacho(0);
          } else {
            setSinCobertura(false);
            setEnvioGratis(costo === 0);
            setCostoDespacho(costo);
          }
        } else {
          console.error('Error en DistanceMatrix:', status);
          setDistanciaKm(null);
          setSinCobertura(false);
          setEnvioGratis(false);
          setCostoDespacho(0);
        }
      }
    );
  };
  // ===========================================

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.formatted_address) {
      setDireccion(place.formatted_address);
      calcularDistancia(place.formatted_address);
    }
    if (place?.geometry?.location && map) {
      const loc = place.geometry.location;
      map.panTo(loc);
      map.setZoom(16);
      if (!marker) {
        const newMarker = new window.google.maps.Marker({ position: loc, map });
        setMarker(newMarker);
      } else {
        marker.setPosition(loc);
      }
    }
  };

  const subtotal = carrito.reduce((acc, item) => acc + item.precio * item.cantidad, 0);
  const descuento = estadoCupon === 'valido' ? 5000 : 0;
  const total = subtotal + (tipoEntrega === 'despacho' ? costoDespacho : 0) - descuento;

  const handleValidarCupon = () => {
    setEstadoCupon(codigoDescuento.trim().toUpperCase() === 'PRIMERCMP' ? 'valido' : 'invalido');
  };

  // --- helpers de teléfono (CHL 9 dígitos, sin +56) ---
  const soloDigitos = (s: string) => s.replace(/\D/g, '');
  const handleTelefonoChange = (v: string) => {
    // Si ingresan +569..., nos quedamos con los últimos 9 dígitos
    const d = soloDigitos(v);
    const ult9 = d.length > 9 ? d.slice(-9) : d;
    setTelefono(ult9);
  };
  const telefonoValido = telefono.length === 9; // ej. 942380631

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // validación mínima
    if (!telefonoValido) {
      alert('Ingresa un teléfono válido de 9 dígitos (ej: 942380631).');
      return;
    }

    // bloquear envío si está fuera de cobertura
    if (tipoEntrega === 'despacho' && sinCobertura) {
      alert('Fuera de cobertura de despacho (más de 15 km). Elige retiro o una dirección más cercana.');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Sesión expirada. Inicia sesión de nuevo.');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/crear_orden/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          carrito,
          total,
          telefono, // <-- se envía al backend
          direccion: `${direccion}, ${numero}`,
          metodo_pago: metodoPago,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        console.error('Error backend:', errData);
        alert('Error en backend: ' + JSON.stringify(errData));
        return;
      }

      const data = await response.json();
      console.log('Respuesta del backend:', data);

      if (metodoPago === 'mercadopago') {
        if (data.init_point) {
          window.location.href = data.init_point;
        } else {
          alert('No se recibió init_point');
        }

      } else if (metodoPago === 'transferencia') {
        if (data.orden_id) {
          navigate('/checkout-transferencia', {
            state: { ordenId: data.orden_id, total, carrito },
          });
        } else {
          alert('No se recibió el ID de la orden');
        }

      } else if (metodoPago === 'efectivo') {
        if (data.orden_id) {
          // Redirige a éxito y que el backend entregue el mensaje
          navigate(`/checkout-success?metodo=efectivo&orden_id=${data.orden_id}`);
        } else {
          alert('No se recibió el ID de la orden');
        }

      } else if (metodoPago === 'flow') {
        if (data.payment_url) {
          window.location.href = data.payment_url; // redirección a Flow
        } else {
          alert('No se recibió el link de pago de Flow');
        }
      }
    } catch (err) {
      console.error(err);
      alert('Error al procesar el pedido');
    }
  };

  if (!isLoaded) return <p>Cargando mapa...</p>;

  return (
    <div className="pago-container">
      <h2>Completa tu pedido</h2>

      <form onSubmit={handleSubmit} className="formulario-pago">
        <label>
          Tipo de entrega:
          <select
            value={tipoEntrega}
            onChange={(e) => setTipoEntrega(e.target.value as 'despacho' | 'retiro')}
            required
          >
            <option value="despacho">Despacho a domicilio</option>
            <option value="retiro">Retiro en Metro Santa Isabel</option>
          </select>
        </label>

        {/* TELÉFONO (siempre visible) */}
        <label>
          Teléfono de contacto (9 dígitos):
          <input
            type="tel"
            inputMode="numeric"
            placeholder="942380631"
            value={telefono}
            onChange={(e) => handleTelefonoChange(e.target.value)}
            pattern="\d{9}"
            maxLength={9}
            required
            style={{ width: '100%' }}
          />
          {!telefonoValido && telefono !== '' && (
            <small style={{ color: '#b91c1c' }}>Debe tener exactamente 9 dígitos.</small>
          )}
        </label>

        {tipoEntrega === 'retiro' && (
          <div className="retiro-info">
            📍 Retiro disponible en <strong>Metro Santa Isabel</strong>
          </div>
        )}

        {tipoEntrega === 'despacho' && (
          <>
            <label>
              Dirección:
              <Autocomplete
                onLoad={(auto) => (autocompleteRef.current = auto)}
                onPlaceChanged={handlePlaceChanged}
              >
                <input
                  type="text"
                  placeholder="Calle y número"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                  required
                  style={{ width: '100%' }}
                />
              </Autocomplete>
            </label>

            <label>
              Número / Departamento:
              <input
                type="text"
                placeholder="Número o depto"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
              />
            </label>

            {/* ⬇️ Mensaje/costo respetando tu bloque y estilos */}
            <div className="costo-despacho">
              {sinCobertura ? (
                <strong style={{ color: '#b91c1c' }}>
                  No hay cobertura de despacho (&gt; 15 km).
                </strong>
              ) : envioGratis ? (
                <strong style={{ color: '#16a34a' }}>
                  Envío gratis (distancia &lt; 5 km)
                </strong>
              ) : (
                <>
                  <strong>Costo de despacho:</strong> ${costoDespacho.toLocaleString()}
                </>
              )}
              {typeof distanciaKm === 'number' && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Distancia estimada: {distanciaKm.toFixed(1)} km
                </div>
              )}
            </div>
          </>
        )}

        {/* Mapa (solo visible en despacho) */}
        <div
          id="map"
          style={{
            width: '100%',
            height: '300px',
            display: tipoEntrega === 'despacho' ? 'block' : 'none',
            marginTop: '20px',
          }}
          ref={(node) => {
            if (node && !map) {
              const m = new window.google.maps.Map(node, {
                center,
                zoom: 12,
                disableDefaultUI: true,
                draggable: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                gestureHandling: 'none',
              });
              setMap(m);
            }
          }}
        />

        <label>
          Código de descuento:
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Ingresa tu código"
              value={codigoDescuento}
              onChange={(e) => {
                setCodigoDescuento(e.target.value);
                setEstadoCupon(null);
              }}
              style={{ flex: 1 }}
            />
            <button type="button" onClick={handleValidarCupon}>
              Validar
            </button>
          </div>
        </label>

        {estadoCupon === 'valido' && <div style={{ color: 'green' }}>🎉 Cupón agregado</div>}
        {estadoCupon === 'invalido' && <div style={{ color: 'red' }}>❌ Cupón no es válido</div>}

        <h3>Método de pago</h3>

        {(() => {
          type MetodoPago = 'flow' | 'mercadopago' | 'transferencia' | 'efectivo';

          const metodos: {
            id: MetodoPago;
            title: string;
            subtitle: string;
            color: string;
            icon: React.ReactNode;
            badge?: string;
          }[] = [
              {
                id: 'flow',
                title: 'Flow',
                subtitle: 'Webpay, Onepay, Débito y Crédito',
                color: '#16a34a',
                icon: <FaCreditCard size={22} />,
                badge: 'Recomendado',
              },
              {
                id: 'transferencia',
                title: 'Transferencia bancaria',
                subtitle: 'Banco a banco — recibirás los datos al confirmar',
                color: '#3b82f6',
                icon: <FaUniversity size={22} />,
              },
              {
                id: 'efectivo',
                title: 'Efectivo contra entrega',
                subtitle: 'Pagas al recibir tu pedido · Solo RM',
                color: '#f59e0b',
                icon: <FaMoneyBillWave size={22} />,
              },
            ];

          return (
            <div className="pm-list" role="radiogroup" aria-label="Método de pago">
              {metodos.map(m => {
                const selected = metodoPago === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setMetodoPago(m.id)}
                    className={`pm-option${selected ? ' pm-selected' : ''}`}
                    style={{ ['--pm-color' as any]: m.color }}
                  >
                    <span className="pm-icon" style={{ color: m.color }}>
                      {m.icon}
                    </span>
                    <span className="pm-info">
                      <span className="pm-title">
                        {m.title}
                        {m.badge && <span className="pm-badge">{m.badge}</span>}
                      </span>
                      <span className="pm-subtitle">{m.subtitle}</span>
                    </span>
                    <span className={`pm-check${selected ? ' pm-check--on' : ''}`}>
                      {selected && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })()}


        <div className="total-pago">
          {estadoCupon === 'valido' ? (
            <>
              <div><strong>Total:</strong> ${subtotal.toLocaleString()}</div>
              {tipoEntrega === 'despacho' && (
                <div><strong>Despacho:</strong> ${costoDespacho.toLocaleString()}</div>
              )}
              <div style={{ color: 'green' }}><strong>Descuento:</strong> -$5,000</div>
              <div><strong>Total a pagar:</strong> ${total.toLocaleString()}</div>
            </>
          ) : (
            <>
              {tipoEntrega === 'despacho' ? (
                <>
                  <div><strong>Subtotal:</strong> ${subtotal.toLocaleString()}</div>
                  <div><strong>Despacho:</strong> ${costoDespacho.toLocaleString()}</div>
                  <div><strong>Total a pagar:</strong> ${total.toLocaleString()}</div>
                </>
              ) : (
                <div><strong>Total a pagar:</strong> ${total.toLocaleString()}</div>
              )}
            </>
          )}
        </div>

        <button className="btn-secundario" onClick={() => (window.location.href = '/carrito')}>
          Volver al carrito
        </button>
        <button
          type="submit"
          className="btn-confirmar"
          disabled={tipoEntrega === 'despacho' && sinCobertura}
          title={sinCobertura ? 'Fuera de cobertura de despacho' : undefined}
        >
          Confirmar pedido
        </button>
      </form>
    </div>
  );
};

export default Pago;
